import {
  AdministrationMode,
  CatalogType,
  DISCARD_REST_VIAL_REASONS,
  DISCARD_RETURN_REASONS,
  DoseCalcModeActive,
  PatientTherapySequenceStatus,
  PreparationType,
  Sex,
  StockMovementSource,
  StockMovementType,
} from '@cypro/engine/database/schema'
import {PRESCRIPTION_REPORT_FIELDS, STOCK_MOVEMENT_REPORT_FIELDS} from '@cypro/engine/reports'
import {type TranslateFn} from '@cypro/engine/reports'
import * as sdk from '@cypro/server/sdk'

/** The request and response types come straight from the SDK, generated off the routes. */
export type ReportExportBody = sdk.PostReportsExportData['body']
export type ReportExportPreviewBody = sdk.PostReportsExportPreviewData['body']
export type ExportLabels = ReportExportBody['labels']
/** One row of the "recent exports" list, as the list route returns it. */
export type ReportExport = sdk.GetReportsExportResponses['200']['exports'][number]

/**
 * The labels the server needs to render the file: every column header plus the enum values the
 * formatters translate. Enumerated from the engine enums so the file reads in the user's language.
 */
export function buildExportLabels(t: {report: TranslateFn; common: TranslateFn; activity: TranslateFn}): ExportLabels {
  const report: Record<string, string> = {}
  for (const field of [...PRESCRIPTION_REPORT_FIELDS, ...STOCK_MOVEMENT_REPORT_FIELDS]) {
    report[field.labelKey] = t.report(field.labelKey)
  }
  for (const value of StockMovementType.options) report[`movement_type_${value}`] = t.report(`movement_type_${value}`)
  for (const value of new Set([...DISCARD_REST_VIAL_REASONS, ...DISCARD_RETURN_REASONS])) {
    report[`discard_reason_${value}`] = t.report(`discard_reason_${value}`)
  }
  for (const value of StockMovementSource.options) report[`source_${value}`] = t.report(`source_${value}`)
  for (const key of [
    'origin_protocol',
    'origin_individual',
    'prior_approval_not_required',
    'prior_approval_pending',
    'prior_approval_approved',
    'weight_type_aibw',
    'weight_type_actual',
    'scan_check_completed',
    'scan_check_patient_skipped',
    'units',
  ]) {
    report[key] = t.report(key)
  }

  const common: Record<string, string> = {yes: t.common('yes'), no: t.common('no')}
  for (const value of [...Sex.options, ...PreparationType.options, ...CatalogType.options, ...DoseCalcModeActive.options]) {
    common[value] = t.common(value)
  }
  for (const value of AdministrationMode.options) {
    const key = `settings.administrationMode.${value.toLowerCase()}`
    common[key] = t.common(key)
  }

  const activity: Record<string, string> = {}
  for (const key of [
    ...PatientTherapySequenceStatus.options,
    'validating.pharmacistCheck',
    'validating.patientArrival',
  ]) {
    activity[`${key}.title`] = t.activity(`${key}.title`)
  }

  return {report, common, activity}
}

/** Row count for the current filters (Zero cannot count, so the server runs the same query capped). */
export async function fetchReportExportPreview(
  body: ReportExportPreviewBody
): Promise<sdk.PostReportsExportPreviewResponses['200']> {
  const {data, error} = await sdk.postReportsExportPreview({body})
  if (error) throw error
  if (!data) throw new Error('Invalid export preview response')
  return data
}

/** The requester's own exports, newest first. */
export async function fetchMyReportExports(): Promise<ReportExport[]> {
  const {data, error} = await sdk.getReportsExport({})
  if (error) throw error
  return data?.exports ?? []
}

/** Marks exports as announced, so a finished one is only toasted once. */
export async function acknowledgeReportExports(ids: string[]): Promise<void> {
  const {error} = await sdk.postReportsExportAcknowledge({body: {ids}})
  if (error) throw error
}

/** Downloads a finished background export. */
export async function downloadQueuedReportExport(id: string): Promise<void> {
  const {response, data, error} = await sdk.getReportsExportByIdDownload({path: {id}, parseAs: 'blob'})
  if (error) throw error
  if (!(data instanceof Blob)) throw new Error('Invalid export response')

  saveBlob(data, response ? filenameFrom(response, `Cypro_export_${id}`) : `Cypro_export_${id}`)
}

function filenameFrom(response: Response, fallback: string): string {
  const match = response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)
  return match?.[1] ?? fallback
}

/** Saves a blob through a temporary link; a PDF is opened in a new tab so it can be printed. */
function saveBlob(blob: Blob, filename: string, openInTab = false) {
  const url = URL.createObjectURL(blob)

  if (openInTab) {
    window.open(url, '_blank')
    return
  }

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Downloads the file. Above the server's threshold the export runs in the background instead, and
 * the id of the queued export comes back so the dialog can offer it when it is ready.
 */
export async function downloadReportExport(body: ReportExportBody): Promise<{exportId?: string}> {
  // the client picks its parser from the content type, so a CSV would come back as text; ask for the bytes
  const {response, data, error} = await sdk.postReportsExport({body, parseAs: 'blob'})
  if (error) throw error

  // `parseAs: 'blob'` applies to the 202 too, so the queued id has to be read out of the body
  if (response?.status === 202) {
    const text = data instanceof Blob ? await data.text() : String(data)
    return {exportId: (JSON.parse(text) as {exportId: string}).exportId}
  }

  if (!(data instanceof Blob)) throw new Error('Invalid export response')
  const fallback = `Cypro_${body.tab}.${body.format}`
  saveBlob(data, response ? filenameFrom(response, fallback) : fallback, body.format === 'pdf')
  return {}
}
